import { MapProperties, UserActivityManager } from '@datum-map';
import {
  UserActivityFeature,
  UserActivityValue,
  UserTrigger,
} from '@datum-map/types';
import { action, autorun, computed, makeObservable, observable } from 'mobx';

type Events = 'left_click' | 'hover';

type EventHandler = (v: UserActivityFeature) => void;

export class MouseController {
  private _userActivity: UserActivityManager;
  private _mapProperties: MapProperties;
  private _target?: HTMLElement;
  private _leftClickHandlers = new Set<EventHandler>();
  private _hoverHandlers = new Set<EventHandler>();

  @observable private _isActive = false;

  constructor(userActivity: UserActivityManager, prop: MapProperties) {
    this._userActivity = userActivity;
    this._mapProperties = prop;
    this.enable();

    makeObservable(this);

    autorun(() => this._setTarget(this._mapProperties.target));
  }

  @computed get isActive() {
    return this._isActive;
  }

  @action enable = () => {
    if (this._isActive) return;

    this._userActivity.subscribe(this._handleLeftClick, UserTrigger.LEFT_CLICK);
    this._userActivity.subscribe(this._handleHover, UserTrigger.HOVER);
    this._isActive = true;
  };

  @action disable = () => {
    if (!this._isActive) return;

    this._userActivity.unsubscribe(
      this._handleLeftClick,
      UserTrigger.LEFT_CLICK,
    );
    this._userActivity.unsubscribe(this._handleHover, UserTrigger.HOVER);
    this._isActive = false;
  };

  on = (event: Events, cb: EventHandler) => {
    if (event === 'left_click') this._leftClickHandlers.add(cb);
    if (event === 'hover') this._hoverHandlers.add(cb);
  };

  un = (event: Events, cb: EventHandler) => {
    if (event === 'left_click') this._leftClickHandlers.delete(cb);
    if (event === 'hover') this._hoverHandlers.delete(cb);
  };

  // TODO: Добавить возможность игнорировать временные слои (Иначе временная подсветка фичи, будет блокировать доступ к основной фичи)
  private _handleLeftClick = (event: UserActivityValue) => {
    const targetData = event.features[0];

    if (!targetData) return;

    this._leftClickHandlers.forEach((cb) => {
      cb(targetData);
    });
  };

  private _handleHover = (value: UserActivityValue) => {
    if (!this._target) return;

    const feature = value.features[0];

    if (!feature) {
      this._target.style.cursor = 'default';

      return;
    }

    this._target.style.cursor = 'pointer';

    this._hoverHandlers.forEach((cb) => {
      cb(feature);
    });
  };

  private _setTarget(target: HTMLElement | string | undefined) {
    if (target instanceof HTMLElement) {
      this._target = target;

      return;
    }

    if (typeof target === 'string') {
      const docElem = document.getElementById(target);

      if (docElem !== null) {
        this._target = docElem;

        return;
      }
    }

    this._target = undefined;
  }
}
